import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { handleRematchResolved } from './rematchResolution';
import { apiClient } from '../network/ApiClient';
import Phaser from 'phaser';
import { recordGameExit } from '../debug/exitBreadcrumbs';
import { resolveSessionId } from '../network/sessionIdentity';
import { useTranslation } from '../i18n/react';
import { getPlayerName } from '../utils/playerName';
import { GameScene } from '../scenes/GameScene';
import { createSpectatorGameTransport, type GameTransport } from '../network/gameTransport';
import { RoundEndOverlay } from '../components/game/RoundEndOverlay';
import { OrdinaryRoundEndOverlay } from '../components/game/OrdinaryRoundEndOverlay';
import { MatchEndOverlay } from '../components/game/MatchEndOverlay';
import { IS_MOBILE_EXPERIENCE } from '../utils/deviceMode';
import { useAuth } from '../context/AuthContext';
import { useFriends } from '../context/FriendContext';
import { uiFromError, renderUiMessage } from '../i18n/uiMessage';
import { showSocialToast } from '../components/social/SocialToastManager';
import { useIdentity, resolveDisplayName } from '../hooks/useIdentity';
import { useLeaveGameGuard } from '../hooks/useLeaveGameGuard';
import type { GameMode, GamePlayersUpdateDetail } from '../types/game';
import { useChatState } from '../hooks/useChatState';
import { MatchCard } from '../components/game/MatchCard';
import { PlayersCard, type PlayerRow } from '../components/game/PlayersCard';
import { GameChatSection } from '../components/game/GameChatSection';
import { useRoundEndOverlay } from '../hooks/useRoundEndOverlay';
import { DS2 } from '../theme/designTokens';
import { stageSquareSide, stageAvailableHeight } from '../utils/stageSquare';
import { GS_INFO_COL, GS_AD_COL } from './gameScreenGrid';
// Cosmetics: the authoritative source is the backend's viewer-scoped
// `viewer_cosmetics` on the game-state payload (resolved server-side from
// the session's host theme / this seat's personal selection, both seeded
// from account preferences at session creation/join — Settings V1). It is
// normalized by bootCosmeticsFromGameState and identity-stabilized by
// stableBootCosmetics so value-equal per-round refetches never reboot
// Phaser, then fed to __GAME_INIT__/RoundEndOverlay via `bootCosmetics`.
import { bootCosmeticsFromGameState, stableBootCosmetics, type ViewerTileSkin, type ViewerTableSkin } from '../config/viewerCosmetics';
import { PreferenceSettings } from '../config/preferences';

// ── Desktop HUD shape emitted by GameScene ────────────────────────
interface HudState {
  usScore:  number;
  themScore: number;
}

// Desktop stage background — arena feel: warm dark base, gold table glow, edge vignette.
// #080600 base: barely perceptible warmth vs pure #000, reads as a room not a void.
// Gold radial (7%) gives the table area a lit-surface quality.
// Vignette (55%) pulls focus inward without adding visible UI.
const HERO_GRADIENT =
  'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.07) 0%, transparent 60%), ' +
  'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%), ' +
  '#080600';

/**
 * Content width (of `.gs-grid`) below which the screen stacks. Because the
 * game now lives inside AppShell's sidebar shell (190px full / 72px collapsed),
 * the real available width is viewport − sidebar — so the responsive switch is
 * driven by CONTAINER width (container queries + a measured grid ref), never
 * the viewport. Must match the Tier-B `@container` min-width below.
 */
const GS_STACK_CONTENT = 760;

/**
 * Responsive game-screen grid — content-container driven (NOT viewport media
 * queries). `.gs-container` establishes an inline-size container context and
 * `.gs-grid` reflows against ITS width. One tree for all sizes; nothing
 * remounts on resize.
 *
 * Tiers by CONTENT width (viewport − sidebar):
 *   ≥1100   wide desktop            [ stage | info | ad ]
 *   760–1099 narrow desktop/tablet  [ stage | info ]        (ad hidden)
 *   <760    phone / stacked          stage / chat / (match|players)  (scrolls)
 *   <480     (within stacked)        match & players stack full-width
 *
 * The Phaser square is ALWAYS min(available width, available height) — bounded
 * by the visible height too (see applySquare), so it is never cropped or
 * scrolled-past at any tier. React measures and sizes the mount; Phaser only
 * fills it (Phaser/scene sizing unchanged). In the side-by-side tiers the
 * square is TOP-aligned in its cell (not vertically centered) so a width-bound
 * square sits coherently with the top of the info column instead of floating
 * mid-column; horizontal centering stays with React (Phaser is NO_CENTER).
 *
 * Surfaces: the grid and info column share the application shell surface
 * (DS2.color.surface, #1F1F1F — same token as the Sidebar) so the panel reads
 * as part of the shell, not a pure-black column beside it.
 */
const GS_GRID_CSS = `
.gs-container { container-type: inline-size; container-name: gsroot; width: 100%; height: 100%; }

/* Base = phone / stacked (mobile-first, and the safe fallback if @container is
   unsupported): stage on top, then chat, then Match/Players. Page scrolls.
   Safe-area insets keep panel content reachable on notched devices. */
.gs-grid {
  width: 100%; height: auto; min-height: 100%;
  display: grid; overflow: visible; background: ${DS2.color.surface};
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  grid-template-areas: "stage" "info";
}
.gs-stage { grid-area: stage; width: 100%; display: flex; align-items: flex-start; justify-content: center; overflow: hidden; background: ${HERO_GRADIENT}; }
.gs-info { grid-area: info; height: auto; overflow: visible; background: ${DS2.color.surface}; display: flex; flex-direction: column; padding: 16px max(10px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left)); gap: 16px; }
.gs-chat { order: 1; flex: none; display: flex; flex-direction: column; }
.gs-cards { order: 2; display: flex; flex-direction: row; gap: 10px; align-items: stretch; }
.gs-cards > * { flex: 1; min-width: 0; }
.gs-ad { display: none; }
.gs-ad span { font-size: 60px; color: #000; font-family: sans-serif; }

/* Very narrow content: Match & Players stack full-width (MatchCard's centered
   40px score row can't fit two-up below ~480px). Order stays Phaser, Chat,
   Match, Players. */
@container gsroot (max-width: 479.98px) {
  .gs-cards { flex-direction: column; gap: 12px; }
  .gs-cards > * { flex: none; min-width: 0; }
}

/* Tier B — narrow desktop / tablet landscape: stage | info, ad hidden. The
   square sits beside the info column, bounded by BOTH width and height. The
   info column may scroll internally at short heights rather than clipping
   the chat input. */
@container gsroot (min-width: ${GS_STACK_CONTENT}px) {
  .gs-grid {
    height: 100%; min-height: 0; overflow: hidden;
    grid-template-columns: minmax(0, 1fr) ${GS_INFO_COL}px;
    grid-template-rows: 1fr;
    grid-template-areas: "stage info";
  }
  .gs-info { height: 100%; overflow-y: auto; overflow-x: hidden; padding: 16px 16px 14px; gap: 16px; }
  .gs-cards { order: 0; flex-direction: column; gap: 16px; flex-shrink: 0; }
  .gs-cards > * { flex: none; }
  .gs-chat { order: 0; flex: 1; min-height: 0; }
  .gs-ad { display: none; }
}

/* Tier A — wide desktop: stage | info | ad. */
@container gsroot (min-width: 1100px) {
  .gs-grid {
    grid-template-columns: minmax(0, 1fr) ${GS_INFO_COL}px ${GS_AD_COL}px;
    grid-template-areas: "stage info ad";
  }
  .gs-ad { grid-area: ad; display: flex; align-items: center; justify-content: center; background: #BCF2FF; }
}
`;

/**
 * Stub that replaces MenuScene — fires a window event so React can
 * navigate back to home. Key must stay 'MenuScene' so GameScene's
 * existing scene.start('MenuScene') calls are intercepted here.
 */
class ExitScene extends Phaser.Scene {
  constructor() { super({ key: 'MenuScene' }); }
  create(): void { window.dispatchEvent(new CustomEvent('phaser-exit-to-menu')); }
}

/**
 * First scene Phaser auto-starts. Immediately hands off to GameScene
 * with the correct gameId / sessionId that were injected via the
 * global __GAME_INIT__ object before the Phaser instance was created.
 */
interface GameInit {
  gameId: number;
  sessionId: number;
  useDesktopRender?: boolean;
  viewerTileSkin?: ViewerTileSkin;
  viewerTableSkin?: ViewerTableSkin;
  /** Friend-spectating (read-only) — see GameScreen's `spectate` prop and
   *  network/gameTransport.ts createSpectatorGameTransport. Absent/false for
   *  every real participant, exactly as before this field existed. */
  spectate?: boolean;
  transport?: GameTransport;
}

class InitScene extends Phaser.Scene {
  constructor() { super({ key: 'InitScene' }); }
  create(): void {
    const init = (window as Window & { __GAME_INIT__?: GameInit }).__GAME_INIT__;
    if (init) {
      this.scene.start('GameScene', {
        gameId: init.gameId,
        sessionId: init.sessionId,
        teamScores: {},
        useDesktopRender: init.useDesktopRender ?? false,
        viewerTileSkin: init.viewerTileSkin ?? 'classic',
        viewerTableSkin: init.viewerTableSkin ?? 'green_fabric',
        spectate: init.spectate ?? false,
        transport: init.transport,
      });
    } else {
      recordGameExit('unknown', { note: 'init-scene-missing-game-init' });
      window.dispatchEvent(new CustomEvent('phaser-exit-to-menu'));
    }
  }
}

interface GameScreenProps {
  /** Friend-spectating (read-only). Renders the SAME screen/Phaser scene as
   *  a real participant — see network/gameTransport.ts
   *  createSpectatorGameTransport for why that's safe — but fetches the
   *  hand-blind spectator state, never blocks navigation away / warns on tab
   *  close (useLeaveGameGuard disabled), and hides table chat (private to
   *  the players — product decision). Defaults to false so every existing
   *  /game/:id caller is completely unaffected. */
  spectate?: boolean;
}

export function GameScreen({ spectate = false }: GameScreenProps) {
  const { t }     = useTranslation('gameplay');
  const { id }    = useParams<{ id: string }>();
  const gameId    = Number(id);
  const navigate  = useNavigate();
  const location  = useLocation();
  const gameRef   = useRef<Phaser.Game | null>(null);
  const mountRef  = useRef<HTMLDivElement>(null);
  // The stage cell the mount square is measured from (width, and height when
  // the stage is a bounded grid row).
  const stageRef  = useRef<HTMLDivElement>(null);
  // The full content container — its width drives the responsive tier decision
  // (the stage is only one column inside it, so it can't be used for the tier).
  const gridRef   = useRef<HTMLDivElement>(null);

  // Best-known session id at first paint: nav state → sessionStorage → 0.
  // The AUTHORITATIVE value is the REST GameState's session_id, resolved in the
  // effect below (F4). A direct /game/:id deep link starts at 0 and is healed
  // once state loads; everything that needs the session id reads resolvedSessionId.
  const seedSessionId: number = (() => {
    const fromState = (location.state as { sessionId?: number })?.sessionId ?? 0;
    if (fromState > 0) return fromState;
    const saved = sessionStorage.getItem(`game-session-${gameId}`);
    return saved ? Number(saved) : 0;
  })();

  const [resolvedSessionId, setResolvedSessionId] = useState<number>(seedSessionId);
  // Latest resolved id, readable from once-registered listeners (next-round URL sync).
  const sessionIdRef = useRef<number>(seedSessionId);
  sessionIdRef.current = resolvedSessionId > 0 ? resolvedSessionId : seedSessionId;

  // Persist the seed for refresh recovery (moved out of render into an effect).
  useEffect(() => {
    if (seedSessionId > 0) sessionStorage.setItem(`game-session-${gameId}`, String(seedSessionId));
  }, [gameId, seedSessionId]);

  // Fixed at module load — never switches on browser resize
  const isMobile = IS_MOBILE_EXPERIENCE;

  // ── HUD state (updated by GameScene via 'game-hud-update' event) ──────
  const [hud, setHud] = useState<HudState | null>(null);

  // ── Game type (drives the leave-guard) ────────────────────────────────
  // Fetched once from the authoritative game state. `undefined` until known /
  // if the fetch fails — the leave-guard FAILS CLOSED on undefined: no
  // destructive action is offered until the mode is confirmed (the modal
  // offers a Try-again re-fetch instead).
  const [gameMode, setGameMode] = useState<GameMode | undefined>(undefined);
  // Cosmetics: the viewer's EFFECTIVE skins for this game, resolved from the
  // authoritative game-state fetch (viewer_cosmetics). null until that fetch
  // settles; the Phaser boot below waits for it so BOTH lobby-nav and reconnect
  // start with the correct skins. On fetch failure it falls back to the safe
  // defaults so a game can still boot. This is the real Cosmetics selection
  // SOURCE that replaced the temporary email-based staging selector.
  const [bootCosmetics, setBootCosmetics] = useState<{ tileSkin: ViewerTileSkin; tableSkin: ViewerTableSkin } | null>(null);
  useEffect(() => {
    let cancelled = false;
    // Spectator: the participant endpoint 401s for a non-participant — the
    // spectator endpoint returns the same FullGameStateForUser shape
    // (hand-blind, see Game#game_state_for_spectator), so every field this
    // effect reads below (game_mode, viewer_cosmetics, session_id) resolves
    // identically either way.
    (spectate ? apiClient.getSpectatorGameState(gameId) : apiClient.getGameState(gameId))
      .then(s => {
        if (cancelled) return;
        setGameMode(s.game_mode);
        // Missing viewer_cosmetics / unknown ids → safe Classic + green_fabric.
        // IDENTITY-STABLE (black-frame fix): this effect re-runs for every
        // round's gameId, but same-value cosmetics must return the PREVIOUS
        // reference so the [bootCosmetics] boot effect below never re-fires
        // mid-session — a fresh object here destroyed and rebooted the whole
        // Phaser game between rounds (~1s black canvas).
        setBootCosmetics(prev => stableBootCosmetics(prev, bootCosmeticsFromGameState(s.viewer_cosmetics)));
        // REST session_id is authoritative — heal a deep-link sessionId=0 and
        // persist it so chat, the leave guard, and refresh recovery all agree (F4).
        const authoritative = resolveSessionId(seedSessionId, s.session_id);
        if (authoritative > 0) {
          setResolvedSessionId(authoritative);
          sessionStorage.setItem(`game-session-${gameId}`, String(authoritative));
        }
      })
      .catch(() => {
        // Leave gameMode undefined → the leave-guard fails closed (no
        // destructive action until a retry confirms the mode). If NOTHING is
        // known yet, boot with safe defaults so a failed first lookup never
        // leaves Phaser permanently waiting. But once cosmetics ARE known,
        // KEEP them: a transient per-round refetch failure must not downgrade
        // the skins — that would change the object identity and destroy/reboot
        // Phaser mid-session (the exact black-frame regression this fixes).
        if (!cancelled) setBootCosmetics(prev => prev ?? bootCosmeticsFromGameState(null));
      });
    return () => { cancelled = true; };
  // seedSessionId is read but intentionally not a dep — resolveSessionId prefers
  // the authoritative REST value regardless of the seed at fetch time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, spectate]);

  // ── Round-end overlay ────────────────────────────────────────────────
  // Mounts on phaser:round-end, dismisses on phaser:next-round-ready.
  // All WebSocket subscriptions remain in Phaser — React is UI-only.
  // (Declared before the leave guard so the guard can read the authoritative
  // whole-match completion signal from the payload.)
  const {
    payload: roundEndPayload, markReady, navigateHome, submitRematchVote,
    readyStatus, exiting, onOrdinaryExited,
  } = useRoundEndOverlay();

  // Guard leaving this game (in-app nav, Phaser Leave button, tab close) with
  // mode-specific consequences — solo pause-and-save vs multiplayer forfeit.
  // See the hook; it owns all three exit vectors and the styled confirm modal.
  // matchCompleted (payload.sessionOver) stands the guard down once the WHOLE
  // match is authoritatively over: the rematch flow owns every exit then, and
  // its resolution navigation must not be intercepted by the active-game
  // modal. Ordinary round ends (sessionOver=false) and live play keep full
  // protection; an accepted rematch clears the payload and re-arms the guard
  // for the successor game.
  // Set (via flushSync) when the authoritative REJECTED rematch resolution
  // arrives. The reconnect-into-an-already-rejected-match path resolves
  // WITHOUT a round-end payload, so payload.sessionOver alone cannot stand
  // the guard down there — without this flag the resolution navigation gets
  // intercepted by the "Leave this game?" modal (the observed double-exit
  // defect). Never set during live play; only from phaser:rematch-resolved.
  const [rematchResolved, setRematchResolved] = useState(false);
  useLeaveGameGuard({
    gameMode,
    sessionId: resolvedSessionId,
    gameId,
    gamePath: spectate ? `/spectate/${gameId}` : `/game/${gameId}`,
    matchCompleted: roundEndPayload?.sessionOver === true || rematchResolved,
    // Spectator: never a participant, so nothing can be forfeited/paused —
    // every protection this hook offers stands down (see its own doc comment).
    disabled: spectate,
  });

  // Info-panel chat: collapsed by default (Figma "main" state).
  const [chatOpen, setChatOpen] = useState(false);

  // Settings V1 (Table Chat preference): controls the INITIAL in-game chat
  // presentation only — read once at mount, so mid-game Settings changes
  // never yank the panel, and the live open/collapse state above remains
  // authoritative while visible. Hiding is purely presentational: the
  // ActionCable transport and message history are untouched (useChatState
  // below is unchanged; it already fetches lazily only while chat is open).
  const [chatVisible] = useState(() => PreferenceSettings.getTableChatEnabled());

  // Responsive layout switch — stacked when the CONTENT container is narrower
  // than GS_STACK_CONTENT. Drives the chat fill mode only; the grid itself
  // reflows via CSS container queries (see GS_GRID_CSS). Measured from the grid
  // container (never the viewport — the sidebar makes them differ), so it
  // reacts to sidebar changes, DevTools resizing and shell transitions.
  const [isNarrow, setIsNarrow] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      const g = gridRef.current;
      if (g) setIsNarrow(g.getBoundingClientRect().width < GS_STACK_CONTENT);
    };
    measure(); // before paint — no flash of the wrong chat mode
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && gridRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(gridRef.current);
    }
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  // Current user's display name — used in the match card title. Canonical
  // chain via the shared resolver (backend display_name first, never an ad
  // hoc email split).
  const { user, devDisplayName } = useAuth();
  const identity = useIdentity();
  const userName = resolveDisplayName(identity, user, devDisplayName || 'Player');

  // ── Chat state ────────────────────────────────────────────────────────
  // Spectator: table chat is private to the players (product decision) — the
  // backend never sends a spectator chat history or live chat_message events
  // in the first place (see SessionChannel's spectator stream), so this stays
  // permanently closed/empty rather than lazily fetching a history that will
  // never arrive. The chat panel itself is also not rendered below.
  const { messages, sendMessage } = useChatState(resolvedSessionId, spectate ? false : chatOpen);

  useEffect(() => {
    const onHud = (e: Event) => {
      const d = (e as CustomEvent).detail;
      setHud({ usScore: d.usScore, themScore: d.themScore });
    };
    window.addEventListener('game-hud-update', onHud);
    return () => window.removeEventListener('game-hud-update', onHud);
  }, []);

  // ── Phaser bootstrap ──────────────────────────────────────────────────
  useEffect(() => {
    // Cosmetics: wait for the authoritative viewer_cosmetics (bootCosmetics) so
    // Phaser boots ONCE with the correct skins on BOTH lobby-nav and reconnect.
    if (!mountRef.current || gameRef.current || !bootCosmetics) return;

    (window as Window & { __GAME_INIT__?: GameInit }).__GAME_INIT__ = {
      gameId,
      sessionId: sessionIdRef.current,
      useDesktopRender: isMobile,
      viewerTileSkin: bootCosmetics.tileSkin,
      viewerTableSkin: bootCosmetics.tableSkin,
      spectate,
      // Constructed fresh once, right before Phaser boots (mirrors how the
      // rest of __GAME_INIT__ is built for a fresh mount) — GameScene itself
      // owns carrying it forward across in-scene round transitions (see
      // GameScene's _isSpectator / scene.start('GameScene', { transport, ... }).
      transport: spectate ? createSpectatorGameTransport(gameId) : undefined,
    };

    // ── Desktop mount sizing: explicit pixel square, set from JS ─────────
    // Deliberately the dumbest mechanism that works. Every CSS-derived
    // approach tried here (height:100% + aspect-ratio + max-width, then
    // container-query units) had a hidden interaction with the canvas Phaser
    // injects (definite-height clamp non-transfer; aspect-ratio's
    // content-based minimum ratcheting off the canvas's own pinned height).
    // Explicit width/height in px leaves the browser nothing to derive and
    // the canvas no way to influence its parent's size. Safe from feedback
    // loops by construction: we READ the stage (whose size is flex-determined
    // by the fixed shell and cannot depend on the mount's content) and WRITE
    // the mount. Same square rule as the scene (squareSideFor).
    const applySquare = () => {
      const grid  = gridRef.current;
      const stage = stageRef.current;
      const mount = mountRef.current;
      if (!grid || !stage || !mount) return;

      // Tier decision from the CONTENT container width (not the stage — the
      // stage is one column of a wide grid; not the viewport — the sidebar
      // makes viewport ≠ available width).
      const stacked = grid.getBoundingClientRect().width < GS_STACK_CONTENT;
      const rect = stage.getBoundingClientRect();

      // Available height + square side via the pure, unit-tested helpers
      // (utils/stageSquare.ts) — visualViewport is dvh-accurate (URL-bar
      // aware), stage.top already includes the mobile top bar.
      const vh = window.visualViewport?.height ?? document.documentElement.clientHeight;
      const availH = stageAvailableHeight(stacked, rect.top, rect.height, vh);

      // INVARIANT: the square is always bounded by BOTH width and height, so it
      // is fully visible (never cropped, never taller than the visible area).
      const side = stageSquareSide(rect.width, availH);
      if (side <= 0) return;
      const px = `${side}px`;
      if (mount.style.width !== px)  mount.style.width  = px;
      if (mount.style.height !== px) mount.style.height = px;
    };
    applySquare(); // size the mount BEFORE Phaser takes its first measurement

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: mountRef.current,
      backgroundColor: '#000000',
      scene: [InitScene, GameScene, ExitScene],
      scale: {
        mode: Phaser.Scale.RESIZE,
        // NO_CENTER is required, not cosmetic. ScaleManager#updateScale() ends
        // by calling updateCenter() on every refresh; updateCenter() reads
        // canvas.getBoundingClientRect() and writes canvas margins from it. In
        // RESIZE mode Phaser sets the canvas width/height ATTRIBUTES (never
        // style), so the measured box is the HiDPI-enlarged attribute box —
        // which produced a bogus margin (the black band above the board) and a
        // poisoned displayScale (the progressively-rightward pointer error).
        // React centers the square instead; see the canvas stage below.
        autoCenter: Phaser.Scale.NO_CENTER,
      },
    };

    const game = new Phaser.Game(config);
    gameRef.current = game;

    // Rendering is deliberately 1:1 (canvas buffer == logical == CSS size).
    // A custom HiDPI supersampling module (renderDPR.ts) lived here through
    // several iterations and was removed: it required holding buffer-sized
    // renderer width/viewport against a logical projection, but Phaser's
    // WebGLRenderer re-derives width/viewport/projection/scissor from the
    // logical scale.baseSize on every refresh and pipelines re-sync their
    // projection from the renderer per bind — the contradiction cannot be
    // maintained cleanly from outside the renderer. Tile sharpness is
    // addressed at the asset level instead (native-resolution tile sets;
    // see config/handTileExperiment.ts).

    // Keeps the mount square and the Phaser canvas in sync with layout.
    // Stage resize (window/panel changes) → recompute the px square; mount
    // resize → refresh Phaser. Writing the mount from the stage callback
    // re-fires the observer once for the mount entry, whose applySquare()
    // writes nothing (same value) — so the chain terminates immediately.
    const resync = () => {
      applySquare();
      gameRef.current?.scale.refresh();
    };
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(resync);
      resizeObserver.observe(mountRef.current);
      if (stageRef.current) resizeObserver.observe(stageRef.current);
      // Observe the content container too — a tier change (container query)
      // resizes the stage column, and the stacked square's height bound tracks
      // the container. Sidebar changes / DevTools resize flow through here.
      if (gridRef.current) resizeObserver.observe(gridRef.current);
    }
    // Visible-viewport changes (mobile URL bar show/hide, dvh) don't resize the
    // stage element, but they change the stacked square's height bound.
    window.visualViewport?.addEventListener('resize', resync);

    const handleExit = () => navigate('/home', { replace: true });
    window.addEventListener('phaser-exit-to-menu', handleExit);

    return () => {
      console.log(`[GameScreen] unmounting — gameId=${gameId} route=${window.location.pathname}`);
      resizeObserver?.disconnect();
      window.visualViewport?.removeEventListener('resize', resync);
      window.removeEventListener('phaser-exit-to-menu', handleExit);
      delete (window as Window & { __GAME_INIT__?: unknown }).__GAME_INIT__;
      // NOTE: the shared ActionCable consumer is NO LONGER disconnected here (F1).
      // GameScreen does not own the global consumer — FriendChannel/presence must
      // survive Home↔Game navigation. GameScene.cleanup() unsubscribes only its
      // own game/session subscriptions; the consumer is torn down centrally on
      // logout/account switch (AuthContext → disconnectCable).
      try {
        game.destroy(true);
      } catch (err) {
        console.error('[GameScreen] Phaser game.destroy() threw during cleanup:', err);
      } finally {
        gameRef.current = null;
      }
    };
  // Boot Phaser exactly ONCE per GameScreen mount (F6). bootCosmetics is
  // IDENTITY-STABLE across rounds (stableBootCosmetics): the per-gameId
  // refetch returns the previous reference when the resolved skins are
  // unchanged, so this effect fires once when cosmetics first settle and
  // never again mid-session — a fresh-but-equal object here previously ran
  // this effect's cleanup and destroyed/rebooted the whole Phaser game on
  // every round transition (the between-rounds black canvas). The scene owns
  // round-to-round transitions internally (scene.start); the browser URL
  // follows via the next-round listener below WITHOUT re-running this effect.
  // A genuinely different cosmetic configuration (different match) still
  // produces a new reference and reboots as intended.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootCosmetics]);

  // ── Next-round URL coherence (F6) ─────────────────────────────────────
  // When Phaser advances to the next game internally, sync the browser URL to
  // /game/<newId> (replace — one session must not push a history entry per round)
  // so a refresh restores the CURRENT round, not round 1. Because the boot effect
  // above does not depend on gameId, this navigation does NOT rebuild Phaser.
  useEffect(() => {
    const onNextRoundReady = (e: Event) => {
      const detail = (e as CustomEvent<{ gameId: number; sessionId?: number }>).detail;
      const newId = detail?.gameId;
      if (!newId || Number.isNaN(newId)) return;
      // Cross-session transition (accepted rematch): adopt the successor
      // session id so chat, the leave guard, and refresh recovery follow it.
      const newSessionId = detail?.sessionId && detail.sessionId > 0 ? detail.sessionId : sessionIdRef.current;
      if (detail?.sessionId && detail.sessionId > 0) {
        setResolvedSessionId(detail.sessionId);
        sessionStorage.setItem(`game-session-${newId}`, String(detail.sessionId));
      }
      if (newId === Number(window.location.pathname.split('/').pop())) return;
      // Spectator: follow the friend into the next round on /spectate/, never
      // /game/ — landing on /game/:id here would 401 (the spectator is not a
      // participant of the new round either) and strand them.
      navigate(`${spectate ? '/spectate' : '/game'}/${newId}`, { replace: true, state: { sessionId: newSessionId } });
    };
    window.addEventListener('phaser:next-round-ready', onNextRoundReady);
    return () => window.removeEventListener('phaser:next-round-ready', onNextRoundReady);
  }, [navigate, spectate]);

  // ── Final-match rematch: REJECTED resolution navigation ───────────────
  // GameScene dispatches phaser:rematch-resolved once (deduped there) after the
  // authoritative rejected broadcast/response. React performs the route change —
  // a single reliable hop that unmounts GameScreen and destroys Phaser. The
  // pure module (rematchResolution.ts) owns the semantics: it translates the
  // legacy '/play/bots' emission to the real bots lobby route ('/play/ai'),
  // stands the leave guard down (flushSync: the blocker predicate reads
  // render-captured state, and navigate() runs it synchronously — the flag
  // must be committed first), and invalidates the global active-game/rejoin
  // state so no destination can show the finished match as resumable.
  useEffect(() => {
    const onRematchResolved = (e: Event) => {
      handleRematchResolved((e as CustomEvent<{ decision: string; route: string }>).detail, {
        markResolved: () => { flushSync(() => setRematchResolved(true)); },
        invalidateActiveGame: () => { window.dispatchEvent(new CustomEvent('active-game-changed')); },
        navigate: (route) => navigate(route, { replace: true }),
      });
    };
    window.addEventListener('phaser:rematch-resolved', onRematchResolved);
    return () => window.removeEventListener('phaser:rematch-resolved', onRematchResolved);
  }, [navigate]);

  // Info-panel player rows (Player Identity V1). GameScene dispatches
  // `phaser:players-update` with the authoritative players array on every
  // gated game state (initial fetch, game updates, round transitions,
  // reconnect); rows render real names/flags/avatars from it. Until the
  // first event arrives, the generic placeholders render — with no flag or
  // avatar slot (identity only appears for real resolved players).
  const [livePlayers, setLivePlayers] = useState<GamePlayersUpdateDetail[] | null>(null);
  useEffect(() => {
    const onPlayers = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (Array.isArray(detail)) setLivePlayers(detail as GamePlayersUpdateDetail[]);
    };
    window.addEventListener('phaser:players-update', onPlayers);
    return () => window.removeEventListener('phaser:players-update', onPlayers);
  }, []);

  // In-game/spectator "Add Friend" (game-context friend request) — see toRow
  // and handleAddFriend below. `friends` comes from FriendProvider, already
  // loaded/polled app-wide. sentFriendRequestIds is session-scoped only (not
  // persisted): a fresh mount re-shows "add" even for a target requested
  // earlier, exactly like FriendsDS2Screen's search-result rows.
  const { friends, sendFriendRequestFromGame } = useFriends();
  const [sentFriendRequestIds, setSentFriendRequestIds] = useState<Set<number>>(new Set());
  const pendingAddFriendRef = useRef<Set<number>>(new Set());

  // In-game/spectator "Add Friend": hide the action for the viewer's own
  // row, bots, and players who are already an accepted/pending friend (the
  // friends list is already loaded app-wide by FriendProvider). `addSent`
  // additionally swaps the button for a disabled "Requested" label for the
  // rest of this screen's lifetime once a request has actually gone out —
  // same session-scoped convention as FriendsDS2Screen's search-result rows.
  const friendUserIds = new Set(friends.map(f => f.friend.id));

  const toRow = (p: GamePlayersUpdateDetail): PlayerRow => ({
    // Shared label seam (playerName.ts): localized bot labels via the stable
    // bot_difficulty field, verbatim names for humans/unknown — keeps the
    // React rows and Phaser seat cards in agreement.
    label:       getPlayerName(p, t('fallback.player')),
    isMe:        p.is_me,
    isBot:       p.is_bot,
    countryCode: p.country_code ?? null,
    avatarKey:   p.avatar_key ?? null,
    resolved:    true,
    userId:      p.id,
    canAdd:      !p.is_me && !p.is_bot && !friendUserIds.has(p.id),
    addSent:     sentFriendRequestIds.has(p.id),
  });

  const teamA: PlayerRow[] = livePlayers
    ? livePlayers.filter(p => p.team === 0).sort((a, b) => a.position - b.position).map(toRow)
    : [{ label: t('fallback.you'), isMe: true }, { label: t('fallback.partner'), isMe: false }];
  const teamB: PlayerRow[] = livePlayers
    ? livePlayers.filter(p => p.team === 1).sort((a, b) => a.position - b.position).map(toRow)
    : [{ label: t('fallback.opponent'), isMe: false }, { label: t('fallback.opponent'), isMe: false }];

  // Spectator: MatchCard's title normally names the VIEWER ("<you> Table to
  // 100") — meaningless for a spectator, who isn't seated. Name the table
  // after seat 0 instead (same anchor=0 convention GameScene uses for the
  // spectator seating layout) so the title reads e.g. "dev1 Table to 100"
  // rather than the spectator's own name. Falls back to the viewer's name
  // only for the brief window before the first players-update event arrives.
  const tableOwner = spectate ? (livePlayers?.find(p => p.position === 0) ?? null) : null;

  const handleAddFriend = (row: PlayerRow) => {
    const targetUserId = row.userId;
    if (targetUserId == null || pendingAddFriendRef.current.has(targetUserId)) return;
    pendingAddFriendRef.current.add(targetUserId);
    void (async () => {
      try {
        await sendFriendRequestFromGame(targetUserId, gameId);
        setSentFriendRequestIds(prev => new Set(prev).add(targetUserId));
      } catch (e) {
        showSocialToast(renderUiMessage(t, uiFromError(e, 'players.addError')));
      } finally {
        pendingAddFriendRef.current.delete(targetUserId);
      }
    })();
  };

  // Spectator no-ops for the round-end/match-end overlays' participant-only
  // actions (Ready / rematch vote) — same signatures as markReady /
  // submitRematchVote so clicking does nothing instead of firing a doomed
  // network call the backend would reject anyway (see SessionChannel's
  // spectator guards). onBackHome/onOrdinaryExited are never swapped — a
  // spectator leaving this screen navigates away exactly like anyone else.
  const noopReady = async (_sessionId: number): Promise<void> => {};
  const noopRematchVote = async (_sessionId: number, _vote: 'yes' | 'no'): Promise<void> => {};

  return (
    <>
      {/* End-state routing: a completed match WITH a rematch snapshot (every
          naturally completed match on the current backend) renders the
          redesigned MatchEndOverlay; a legacy sessionOver terminal WITHOUT a
          snapshot keeps the existing RoundEndOverlay fallback — the existing
          Back-Home handler, byte-identical, never inventing rematch UI.
          Ordinary rounds render OrdinaryRoundEndOverlay with the ready-state
          machine and exit-handshake pieces (unchanged). */}
      {/* Spectator: the round-end/match-end scoreboard is still genuinely
          useful to watch, but Ready/rematch-vote are participant-only
          actions (the backend rejects them for a spectator — see
          SessionChannel's spectator guards) — swapped for no-ops here so
          clicking does nothing instead of firing a doomed network call.
          onBackHome/onOrdinaryExited stay real: a spectator leaving this
          screen should navigate away exactly like anyone else (the
          leave-guard is already fully disabled for spectate — see
          useLeaveGameGuard above — so there is no forfeit consequence to
          protect against here). */}
      {roundEndPayload && (roundEndPayload.sessionOver ? (
        roundEndPayload.rematch ? (
          <MatchEndOverlay
            payload={roundEndPayload}
            onRematchVote={spectate ? noopRematchVote : submitRematchVote}
          />
        ) : (
          <RoundEndOverlay
            payload={roundEndPayload}
            onReady={spectate ? noopReady : markReady}
            onBackHome={navigateHome}
            onRematchVote={spectate ? noopRematchVote : submitRematchVote}
            viewerTileSkin={bootCosmetics?.tileSkin ?? 'classic'}
          />
        )
      ) : (
        <OrdinaryRoundEndOverlay
          payload={roundEndPayload}
          onReady={spectate ? noopReady : markReady}
          readyStatus={readyStatus}
          exiting={exiting}
          onOrdinaryExited={onOrdinaryExited}
          viewerTileSkin={bootCosmetics?.tileSkin ?? 'classic'}
        />
      ))}

      <style>{GS_GRID_CSS}</style>

      {/* Container-query context: `.gs-grid` reflows against THIS element's
          width (= content area, viewport − sidebar), not the viewport. */}
      <div className="gs-container">
        <div className="gs-grid" ref={gridRef}>
          {/* Game square — Phaser mount; React sizes it to a fully-visible
              square (min of available width & height). */}
          <div className="gs-stage" ref={stageRef}>
            <div ref={mountRef} />
          </div>

          {/* Info column — reflows internally: desktop/tablet = cards then chat;
              stacked = chat then Match/Players (side by side ≥480px content,
              stacked below). See GS_GRID_CSS. */}
          <div className="gs-info">
            <div className="gs-cards">
              <MatchCard
                playerName={tableOwner ? getPlayerName(tableOwner, t('fallback.player')) : userName}
                countryCode={tableOwner ? tableOwner.country_code ?? null : identity?.countryCode}
                target={100}
                usScore={hud?.usScore ?? 0}
                themScore={hud?.themScore ?? 0}
              />
              <PlayersCard teamA={teamA} teamB={teamB} onAdd={handleAddFriend} />
            </div>

            {/* Table chat is private to the players (product decision) — a
                spectator never sees this panel at all, not even collapsed. */}
            {chatVisible && !spectate && (
              <div className="gs-chat">
                <GameChatSection
                  messages={messages}
                  sendMessage={sendMessage}
                  open={chatOpen}
                  onToggle={() => setChatOpen(o => !o)}
                  fill={!isNarrow}
                />
              </div>
            )}
          </div>

          {/* AD — wide desktop only (hidden below Tier A via CSS) */}
          <div className="gs-ad"><span>ad</span></div>
        </div>
      </div>
    </>
  );
}
